// v19.0 — Producer Package paywall modal.
// Distinct from the credit-pack BillingModal — this one appears when the
// backend returns 402 on a project create/open attempt (i.e. the user
// has already used their free trial beat) and pitches the Producer
// Package upgrade FIRST. Falls back to /billing/checkout with the
// `producer_monthly` pack id so the existing Stripe flow handles the
// actual payment.
import React, { useState } from "react";
import { X, Lock, Check, SpinnerGap, Sparkle } from "@phosphor-icons/react";
import API from "../api";

const FEATURES = [
  "Unlimited beat projects",
  "Full 1:1 WAV & MP3 export",
  "Per-track FX chain (EQ, Delay, Reverb, Crow Pumper)",
  "Vocal recording with mic input",
  "Stem export (per-track WAVs bundled)",
];

export default function ProducerPaywallModal({ open, onClose, headline, subline }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const checkout = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await API.post("/billing/checkout", {
        pack_id: "producer_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="producer-paywall-modal"
    >
      <div
        className="relative w-full max-w-[520px] rounded-2xl p-7 text-white"
        style={{
          background: "linear-gradient(180deg,#0f0f0f,#050505)",
          border: "1.5px solid #34d39955",
          boxShadow: "0 30px 60px rgba(0,0,0,0.6), 0 0 40px rgba(52,211,153,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white"
          data-testid="producer-paywall-close"
          aria-label="Close"
        >
          <X size={18} weight="bold" />
        </button>

        <div className="mb-5">
          <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-4">
            <Lock size={11} weight="fill" /> paywall
          </div>
          <h2
            className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight"
            data-testid="producer-paywall-heading"
          >
            {headline || "Your free beat has been used."}
          </h2>
          <p className="text-white/50 mt-2 text-sm">
            {subline || "Upgrade to the Producer Package to keep making music — unlimited projects, $9.99/month."}
          </p>
        </div>

        <ul className="space-y-2 mb-6" data-testid="producer-paywall-features">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-white/80">
              <Check size={14} weight="bold" className="text-emerald-300 flex-shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={checkout}
          disabled={busy}
          className="w-full py-3.5 rounded-full font-display font-extrabold text-base tracking-wide flex items-center justify-center gap-2 transition disabled:opacity-60"
          style={{
            background: "linear-gradient(180deg,#34d399,#10b981)",
            color: "#03110b",
            boxShadow: "0 0 24px rgba(52,211,153,0.55), 0 6px 16px rgba(0,0,0,0.5)",
          }}
          data-testid="producer-paywall-upgrade"
        >
          {busy ? (
            <>
              <SpinnerGap size={16} weight="bold" className="animate-spin" /> Redirecting to Stripe…
            </>
          ) : (
            <>
              <Sparkle size={16} weight="fill" /> Upgrade — Producer Package · $9.99/mo
            </>
          )}
        </button>

        {err && (
          <div className="text-xs text-red-400 mt-3 text-center" data-testid="producer-paywall-error">{err}</div>
        )}
        <div className="text-[10px] text-white/30 mt-4 font-mono text-center">
          Powered by Stripe · 30-day DAW access · cancel anytime
        </div>
      </div>
    </div>
  );
}
